import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import lodash from 'lodash';

import { getDelegationsUnderPI, } from 'pages/delegationOfAuthority/selectors';
import { removeNonASCIIChars } from 'pages/delegationOfAuthority/util/removeNonASCIICharsforExport';

const DEFAULT_PI_TESTAMENT = `
I have delegated to each individual above the authority to perform the tasks indicated, under my supervision. 
As of the start date, each individual was qualified to perform the delegated task(s) on the basis of education,
training or experience. (See FDA guidance for Industry Investigator Responsibilities - Protecting the Rights, 
Safety, and Welfare of Study Subjects.)
Electronic Signature(s) Attached`;

const DEFAULT_STAFF_TESTAMENT = `
By signing, I confirm/acknowledge that I accept responsibility for and am appropriately trained, skilled and qualified for the tasks listed herein
Mechanisms are in place to ensure that I receive the appropriate information and training throughout the study and that a 2-way communication channel
exists between P.I. and self.
Electronic Signature(s) Attached`;

const DATE_FORMAT = 'DD MMM YYYY';
const DATE_TIME_FORMAT = 'DD MMM YYYY hh:mm A';
const NOT_APPLICABLE = 'N/A';
const MINOR_SPACING_MM = 2;

const centralizedCellsSize = {
    0: { cellWidth: 55 },
    1: { cellWidth: 60 },
    2: { cellWidth: 30 },
    3: { cellWidth: 55 },
    4: { cellWidth: 30 },
    5: {
        cellWidth: 30,
        halign: 'center'
    },
};

const standardizedCellsSize = {
    0: { cellWidth: 50 },
    1: { cellWidth: 59 },
    2: { cellWidth: 30 },
    3: { cellWidth: 30 },
    4: { cellWidth: 30 },
    5: { cellWidth: 30 },
    6: {
        cellWidth: 30,
        halign: 'center'
    },
};
class DoAExport {
    constructor(timezoneOffset, doaStudyTasks, doaStudyDelegations, binder, testaments, isCentralizedInstitution, state) {
        this.timezoneOffset = timezoneOffset;
        this.doaStudyTasks = doaStudyTasks;
        this.doaStudyDelegations = doaStudyDelegations;
        this.binder = binder;
        this.testaments = testaments;
        this.isCentralizedInstitution = isCentralizedInstitution;
        this.state = state;
    }

    getDateNoTime(date) {
        if (!date) {
            return null;
        }
        const momentDate = moment.utc(date);
        return new Date(momentDate.year(), momentDate.month(), momentDate.date());
    }

    formatDate(date, format) {
        if (lodash.isNil(date)) {
            return '';
        }

        return moment(this.getDateNoTime(date)).format(format);
    }

    formatDateWithTZ(date, format) {
        return moment(date).utcOffset(this.timezoneOffset).format(format);
    }

    writeDoAHeader(pdf, currentPdfLocation, margin) {
        const rightMargin = pdf.internal.pageSize.getWidth() - margin;
        const title = {
            title: `Delegation of Authority - Study ${this.binder.title || ''}`,
        };
        const titleRow = [];
        titleRow.push([title.title]);
        pdf.autoTable({
            margin: { horizontal: margin },
            head: null,
            body: titleRow,
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#F4F5F7';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
                table.cell.styles.cellPadding = 3;
            },
        });
        currentPdfLocation += 13;
        const columns = [['Principal Investigator', 'Protocol/Study Number', 'Sponsor', 'Site Number']];
        const row =
        {
            principalInvestigator: this.binder.primaryInvestigator || '',
            studyNumber: this.binder.trialId || '',
            sponsor: this.binder.sponsorCro || '',
            site: this.binder.departmentSiteDg || '',
        };
        let rows = [];
        rows.push([row.principalInvestigator, row.studyNumber, row.sponsor, row.site,]);
        pdf.autoTable({
            startY: currentPdfLocation + MINOR_SPACING_MM,
            margin: { horizontal: margin },
            columnStyles: { columnWidth: 'wrap' },
            head: columns,
            body: rows,
            headStyles: {
                fillColor: 'white',
                fontStyle: 'bold',
                textColor: 'black',
                fontSize: 10,
            },
            theme: 'plain',
            didDrawPage: function () {
                currentPdfLocation += 20;
                pdf.line(margin, currentPdfLocation, rightMargin, currentPdfLocation);
            }
        });
        return currentPdfLocation + 10;
    }

    writeStatementOfTestament(pdf, currentPdfLocation, margin) {
        pdf.addPage();
        const testaments = [
            {
                title: 'Principal Investigator Testament',
                statement: this.getStatementOfTestament('PI')
            },
            {
                title: 'Staff Testament',
                statement: this.getStatementOfTestament('Staff')
            }
        ];
        testaments.map(testament => {
            const titleRow = [[testament.title]];
            const contentRow = [[testament.statement]];
            pdf.autoTable({
                margin: { horizontal: margin },
                head: titleRow,
                body: contentRow,
                theme: 'plain',
                headStyles: {
                    fontStyle: 'bold',
                    fillColor: '#F4F5F7',
                    textColor: 'black',
                    fontSize: 14,
                    cellPadding: 3
                }
            });
        });
        return currentPdfLocation;
    }

    getMatchingTestament(testaments, title) {
        return lodash.find(testaments, function (o) { return o.title.toUpperCase() === title.toUpperCase(); });
    }

    getStatementOfTestament(title) {
        if (this.testaments && this.getMatchingTestament(this.testaments, title)) {
            const testament = this.getMatchingTestament(this.testaments, title);

            if (title.toUpperCase() === 'STAFF' && this.isCentralizedInstitution) {
                return `${NOT_APPLICABLE}`;
            }

            return removeNonASCIIChars(testament.text);
        }

        if (title.toUpperCase() === 'PI') {
            return DEFAULT_PI_TESTAMENT;
        }
        else if (this.isCentralizedInstitution) {
            return `
            ${NOT_APPLICABLE}`;
        }
        return DEFAULT_STAFF_TESTAMENT;
    }

    writePageHeaders(pdf, exportDate) {
        pdf.setFontSize(8);
        let pageNumber = pdf.internal.getNumberOfPages();
        for (let currentPage = 1; currentPage <= pageNumber; currentPage++) {
            pdf.setPage(currentPage);
            pdf.text(`Page ${currentPage} of ${pageNumber} | Generated ${exportDate}`, 230, 5);
        }
        pdf.setFontSize(12);
    }

    writeStudyKey(pdf, currentPdfLocation, margin) {
        const title = {
            title: 'Delegation Key',
        };
        const titleRow = [];
        titleRow.push([title.title]);
        pdf.autoTable({
            margin: { horizontal: margin },
            head: null,
            body: titleRow,
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#F4F5F7';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
            },
        });
        const columns = [{
            title: 'left',
            setFontSize: 12,
        }, {
            title: 'right',
            setFontSize: 12,
        }];

        currentPdfLocation += 5;
        let endOfTable = currentPdfLocation;
        const studyTasks = this.doaStudyTasks;

        const halfListLength = Math.ceil(studyTasks.length / 2);
        const rows = [];
        let count = halfListLength;
        for (let i = 0; i < halfListLength; i++) { // this forces the numbers to show up vertically, instead of having leftTask 1, rightTask 2 horizontally
            const leftTask = studyTasks[i];

            let rightTask = {
                taskIndex: '',
                taskName: '',
            };

            if (count < studyTasks.length) {
                rightTask = {
                    taskIndex: studyTasks[count].taskIndex,
                    taskName: removeNonASCIIChars(studyTasks[count].taskName),
                };
            }

            let row = {
                left: `${leftTask.taskIndex}. ${removeNonASCIIChars(leftTask.taskName)}`,
                right: rightTask.taskName !== '' ? `${rightTask.taskIndex}. ${rightTask.taskName}` : '',
            };

            rows.push([row.left, row.right]);
            count++;
            endOfTable += margin;
        }

        pdf.autoTable({
            startY: currentPdfLocation,
            margin: { horizontal: margin },
            columnStyles: { columnWidth: 'wrap' },
            head: columns,
            showHead: false,
            body: rows,
            theme: 'plain',
        });
        return endOfTable + 20;
    }

    createStaffStartDate(staffStart, dateFormat) {
        if (this.isCentralizedInstitution) {
            return NOT_APPLICABLE;
        }
        return staffStart && staffStart.signature
            ? this.formatDate(lodash.get(staffStart, 'signature.createdAt'), dateFormat)
            : '';
    }

    delegationHeaderColumns(isCentralized) {
        if (isCentralized) {
            return [['Name Role', 'Delegations', 'Start Date', 'P.I. Signature', 'End Date', 'P.I. Signature']];
        }
        return [['Name Role', 'Delegations', 'Start Date', 'P.I. Signature', 'Staff Signature', 'End Date', 'P.I. Signature']];
    }
    writeDelegationHeader(pdf, titleContent, margin) {
        pdf.autoTable({
            margin: { horizontal: margin },
            head: null,
            body: titleContent,
            didParseCell: function (table) {
                table.cell.styles.fontStyle = 'bold';
                table.cell.styles.fillColor = '#F4F5F7';
                table.cell.styles.textColor = 'black';
                table.cell.styles.fontSize = 14;
                table.cell.styles.cellPadding = 3;
            },
        });
    }

    writeDelegationTable(pdf, delegations, currentPdfLocation, margin, title) {
        const currentPI = lodash.head(delegations.filter(delegation => delegation.role === 'Principal Investigator'));
        const currentPIUserId = lodash.get(currentPI, 'user.id', '');
        const currentPIDisplayName = `Principal Investigator : ${currentPI.user.displayName}`;
        const currentPIStartDateLabel = `from ${this.formatDate(currentPI.startDate, DATE_FORMAT)}`;
        const currentPIEndDateLabel = currentPI.endDate ? `to ${this.formatDate(currentPI.endDate, DATE_FORMAT)}` : `- active as of ${this.formatDate(moment(), DATE_FORMAT)}`;
        let rows = [];
        const header = {
            title: title,
        };

        const headerTitle = {
            title: `${currentPIDisplayName} ${currentPIStartDateLabel} ${currentPIEndDateLabel}`
        };

        const titleRow = [[header.title]];
        const piTitleRow = [[headerTitle.title]];

        this.writeDelegationHeader(pdf, titleRow, margin);
        currentPdfLocation += 10;
        this.writeDelegationHeader(pdf, piTitleRow, margin);
        currentPdfLocation += 15;

        const columns = this.delegationHeaderColumns(this.isCentralizedInstitution);

        delegations.forEach(delegation => {
            const { signatures } = delegation;
            let formattedStaffStartSignatureDate, formattedPIStartSignatureDate, formattedPIEndSignatureDate;
            if (!lodash.isEmpty(signatures)) {
                const sortedSignatures = lodash.orderBy(signatures, 'signature.createdAt', 'desc');
                const staffStart = lodash.head(signatures.filter(signature => signature.signatureType === 'STAFF_START'));
                const piStartSignatures = this.getPISignatureBySignatureType(sortedSignatures, 'PI_START', currentPIUserId);
                const piEndSignatures = this.getPISignatureBySignatureType(sortedSignatures, 'PI_END', currentPIUserId);
                const mostRecentPIStartSignature = lodash.head(piStartSignatures);
                const mostRecentPIEndSignature = lodash.head(piEndSignatures);

                const staffStartSignatureTimestamp = lodash.get(staffStart, 'signature.createdAt', '');
                const piStartSignatureTimestamp = lodash.get(mostRecentPIStartSignature, 'signature.createdAt', false);
                const piEndSignatureTimestamp = lodash.get(mostRecentPIEndSignature, 'signature.createdAt', false);
                formattedStaffStartSignatureDate = staffStartSignatureTimestamp ? this.formatDate(staffStartSignatureTimestamp, DATE_FORMAT) : '';
                formattedPIStartSignatureDate = piStartSignatureTimestamp ? this.formatDate(piStartSignatureTimestamp, DATE_FORMAT) : '';
                formattedPIEndSignatureDate = piEndSignatureTimestamp ? this.formatDate(piEndSignatureTimestamp, DATE_FORMAT) : '';
            }

            const startDate = this.formatDelegationDate(delegation.startDate);
            const endDate = this.formatDelegationDate(delegation.endDate);
            formattedPIEndSignatureDate = lodash.isNil(endDate) || !moment(endDate).isValid() ? NOT_APPLICABLE : formattedPIEndSignatureDate;

            const row = {
                nameAndRole: `${delegation.user.displayName} \n${removeNonASCIIChars(delegation.role)}`,
                tasks: delegation.tasks.map(task => task.taskIndex),
                startDate: startDate,
                piStartTimestamp: formattedPIStartSignatureDate,
                staffStartTimestamp: formattedStaffStartSignatureDate,
                endDate: endDate,
                piEndTimestamp: formattedPIEndSignatureDate,
            };
            this.createRowsContent(rows, row, this.isCentralizedInstitution);
        });

        currentPdfLocation += margin;
        this.createDelegationTable(pdf, columns, rows, margin, currentPdfLocation, this.isCentralizedInstitution);

        return currentPdfLocation += 8;
    }

    formatDelegationDate(date) {
        if (date && moment(date).isValid()) {
            date = this.formatDate(date, DATE_FORMAT);
        }
        return date;
    }

    getPISignatureBySignatureType(signatures, signatureType, PIUserId = null) {
        const sortedSignatures = lodash.orderBy(signatures, 'signature.createdAt', 'desc');
        if (!lodash.isNil(PIUserId)) {
            return sortedSignatures.filter(signature => signature.signatureType === signatureType && lodash.get(signature, 'signature.user.id', '') === PIUserId);
        } else {
            return sortedSignatures.filter(signature => signature.signatureType === signatureType);
        }

    }

    createRowsContent(rows, row, isCentralized) {
        if (isCentralized) {
            rows.push([row.nameAndRole, row.tasks, row.startDate, row.piStartTimestamp, row.endDate, row.piEndTimestamp]);
        } else {
            rows.push([row.nameAndRole, row.tasks, row.startDate, row.piStartTimestamp, row.staffStartTimestamp, row.endDate, row.piEndTimestamp]);
        }
    }

    createDelegationTable(pdf, columns, rows, margin, currentPdfLocation, isCentralized) {
        if (isCentralized) {
            pdf.autoTable({
                startY: currentPdfLocation + MINOR_SPACING_MM,
                margin: { horizontal: margin },
                head: columns,
                body: rows,
                columnStyles: centralizedCellsSize
            });
        } else {
            pdf.autoTable({
                startY: currentPdfLocation + MINOR_SPACING_MM,
                margin: { horizontal: margin },
                head: columns,
                body: rows,
                columnStyles: standardizedCellsSize,
            });
        }
    }

    writeDelegationSignatureTable(pdf, delegations, currentPdfLocation, margin, title) {
        const staff = lodash.head(delegations);
        let rows = [];
        const header = {
            title: title,
        };
        const delegationDisplayName = staff.user.displayName;
        const delegationStartDateLabel = `from ${this.formatDate(staff.startDate, DATE_FORMAT)}`;
        const delegationEndDateLabel = staff.endDate ? `to ${this.formatDate(staff.endDate, DATE_FORMAT)} ` : `- active as of ${this.formatDate(moment(), DATE_FORMAT)}`;
        const headerTitle = {
            title: `${delegationDisplayName} ${delegationStartDateLabel} ${delegationEndDateLabel}`
        };
        const titleRow = [[header.title]];
        const staffRow = [[headerTitle.title]];
        this.writeDelegationHeader(pdf, titleRow, margin);
        currentPdfLocation += 10;

        this.writeDelegationHeader(pdf, staffRow, margin);
        currentPdfLocation += 15;

        const columns = this.delegationHeaderColumns(this.isCentralizedInstitution);
        delegations.forEach(delegation => {
            const { signatures } = delegation;
            let staffStartDate = '';
            let piStartDate = '';
            let piEndDate = '';
            if (signatures) {
                const staffStart = signatures.find(signature => signature.signatureType === 'STAFF_START');
                const piStart = signatures.find(signature => signature.signatureType === 'PI_START');
                const piEnd = signatures.find(signature => signature.signatureType === 'PI_END');
                const latestPIStart = lodash.head(this.getPISignatureBySignatureType(signatures, 'PI_START'));
                const latestPIEnd = lodash.head(this.getPISignatureBySignatureType(signatures, 'PI_END'));

                staffStartDate = this.createStaffStartDate(staffStart, DATE_FORMAT);
                piStartDate =
                    piStart && piStart.signature
                        ? this.formatDate(lodash.get(piStart, 'signature.createdAt'), DATE_FORMAT)
                        : this.formatDate(lodash.get(latestPIStart, 'signature.createdAt'), DATE_FORMAT);
                piEndDate =
                    piEnd && piEnd.signature
                        ? this.formatDate(lodash.get(piEnd, 'signature.createdAt'), DATE_FORMAT)
                        : this.formatDate(lodash.get(latestPIEnd, 'signature.createdAt'), DATE_FORMAT);
            }

            const endDate = delegation.endDate ? this.formatDate(delegation.endDate, DATE_FORMAT) : '';
            const row = {
                nameAndRole: `${delegation.user.displayName} \n${removeNonASCIIChars(delegation.role)}`,
                tasks: delegation.tasks.map(task => task.taskIndex),
                startDate: this.formatDate(delegation.startDate, DATE_FORMAT),
                piStartTimestamp: piStartDate,
                staffStartTimestamp: staffStartDate,
                endDate: endDate,
                piEndTimestamp: piEndDate,
            };
            this.createRowsContent(rows, row, this.isCentralizedInstitution);
        });
        currentPdfLocation += margin;

        this.createDelegationTable(pdf, columns, rows, margin, currentPdfLocation, this.isCentralizedInstitution);

        return currentPdfLocation += 8;
    }

    returnNewPage(currentPdfLocation, pdf) {
        const heightThreshold = 150, heightLimit = 170;

        if (currentPdfLocation % heightLimit < 50 && currentPdfLocation > heightThreshold) {
            pdf.addPage();
            currentPdfLocation = 0;
        }
        return currentPdfLocation;
    }

    writeSignaturePage(pdf, delegation, currentPdfLocation, margin,) {
        const { startDate, endDate, signatures } = delegation;
        const name = delegation.user.displayName;
        const formattedStartDate = this.formatDate(startDate, DATE_FORMAT);
        const formattedEndDate = endDate ? this.formatDate(endDate, DATE_FORMAT) : '';

        const staffStatement = `This is the manifestation of ${name}'s acknowledgement of their delegation start date of ${formattedStartDate}.`;


        if (!this.isCentralizedInstitution) {
            const staffSignature = signatures.find(
                (signature) => signature.signature != null && signature.signatureType === 'STAFF_START'
            );
            currentPdfLocation = this.writeSignature(staffSignature, pdf, currentPdfLocation, staffStatement, 'Staff Testament', margin);
        }

        const piSignatures = signatures.filter(
            (signature) =>
                signature.signature != null &&
                (signature.signatureType === 'PI_START' || signature.signatureType === 'PI_END')
        );
        piSignatures.forEach(piSignature => {
            let piTestament;
            if (piSignature.signatureType === 'PI_START') {
                piTestament = `This is the manifestation of Principal Investigator ${piSignature.signature.user.displayName}'s acknowledgement of ${name}'s delegation start date of ${formattedStartDate}.`;
            } else {
                piTestament = `This is the manifestation of Principal Investigator ${piSignature.signature.user.displayName}'s acknowledgment of ${name}'s delegation end date of ${formattedEndDate}.`;
            }

            currentPdfLocation = this.writeSignature(piSignature, pdf, currentPdfLocation, piTestament, 'Principal Investigator Testament', margin);
        });
        return currentPdfLocation;
    }

    writeSignature(signature, pdf, currentPdfLocation, statement, testamentTitle, margin) {
        if (signature && signature.signature) {
            const signingUserName = signature.signature.user.displayName;
            const signingUserEmail = signature.signature.user.emailAddress;
            const timeStamp = this.formatDateWithTZ(signature.signature.createdAt, DATE_TIME_FORMAT);
            currentPdfLocation += 10;
            currentPdfLocation = this.returnNewPage(currentPdfLocation, pdf);
            currentPdfLocation = this.writeSignatureLine(pdf, signingUserName, signingUserEmail, timeStamp, currentPdfLocation, statement, testamentTitle, margin);
        }
        return currentPdfLocation;
    }

    writeSignatureLine(pdf, name, email, timeStamp, currentPdfLocation, manifestationStatement, testamentSigned, margin) {
        currentPdfLocation += 15;
        const manifestation = {
            testament: manifestationStatement,
        };
        const manifestationRow = [];
        manifestationRow.push([manifestation.testament]);
        const columns = [['Display Name', 'Email Address', 'Date Signed', 'Testament Signed']];
        const row = {
            name: name,
            email: email,
            timeStamp: timeStamp,
            testamentSigned: testamentSigned
        };

        let rows = [];
        rows.push([row.name, row.email, row.timeStamp, row.testamentSigned]);
        pdf.autoTable({
            startY: currentPdfLocation,
            margin: { horizontal: margin },
            head: columns,
            body: rows,
            didDrawPage: function () {
                pdf.autoTable({ // This table exists to prevent the explanation of the signature from escaping its relevant signature area.
                    startY: currentPdfLocation -= 7, // Move the "cursor" above the startY of the outer autotable.
                    margin: { horizontal: margin, vertical: margin },
                    head: null,
                    body: manifestationRow,
                    didParseCell: function (body) {
                        body.cell.styles.fontStyle = 'bold';
                        body.cell.styles.fontSize = '9.5';
                        body.cell.styles.fillColor = '#616161';
                        body.cell.styles.textColor = 'white';
                    },
                });
            }
        });
        return currentPdfLocation + 10;
    }

    delegationHasValidSignatures(delegation) {
        return !lodash.isNil(delegation) &&
            !lodash.isNil(delegation.signatures) &&
            !lodash.isEmpty(delegation.signatures);
    }

    static getDefaultPITestament() {
        return DEFAULT_PI_TESTAMENT;
    }

    static getDefaultStaffTestament() {
        return DEFAULT_STAFF_TESTAMENT;
    }

    getDelegationsByRole(studyDelegations, role) {
        return lodash.compact(studyDelegations.map(delegation => {
            if (delegation.role === role) {
                return delegation;
            }
        }));
    }

    /* 
        Returns staff delegation with start or end date replaced 
        with suitable text based on PI delegation window overlap. 
    */
    manipulateDelegationStartAndEndDates(staffDelegation, piStartDate, piEndDate) {
        let updatedStaffDelegation = lodash.cloneDeep(staffDelegation);
        const staffStartDate = updatedStaffDelegation.startDate;
        const staffEndDate = updatedStaffDelegation.endDate;
        if (this.staffStartedBeforeCurrentPIStartDate(staffStartDate, piStartDate)) {
            lodash.set(updatedStaffDelegation, 'startDate', 'Started under prior P.I.');
            if (this.isCurrentPI(piEndDate)) {
                return updatedStaffDelegation;
            }
            if (this.staffEndedBeforeCurrentPIEndDate(staffEndDate, piEndDate)) {
                return updatedStaffDelegation;
            }
            if (this.staffEndedAfterCurrentPIEndDate(staffEndDate, piEndDate)) {
                if (lodash.isNil(piEndDate)) {
                    return updatedStaffDelegation;
                }
                lodash.set(updatedStaffDelegation, 'endDate', 'Continued under new P.I.');
                return updatedStaffDelegation;
            }
            if (this.isCurrentStaff(staffEndDate)) {
                lodash.set(updatedStaffDelegation, 'endDate', 'Continued under new P.I.');
                return updatedStaffDelegation;
            }
        } else {
            if (this.staffEndedBeforeCurrentPIEndDate(staffEndDate, piEndDate)) {
                return updatedStaffDelegation;
            }
            if (this.staffEndedAfterCurrentPIEndDate(staffEndDate, piEndDate)) {
                lodash.set(updatedStaffDelegation, 'endDate', 'Continued under new P.I.');
                return updatedStaffDelegation;
            }
            if (this.isCurrentStaff(staffEndDate)) {
                lodash.set(updatedStaffDelegation, 'endDate', 'Continued under new P.I.');
                return updatedStaffDelegation;
            }
        }
    }
    isCurrentPI(piEndDate) {
        return lodash.isNil(piEndDate);
    }
    isCurrentStaff(staffEndDate) {
        return lodash.isNil(staffEndDate);
    }

    staffStartedBeforeCurrentPIStartDate(staffStartDate, piStartDate) {
        return moment(staffStartDate).isBefore(piStartDate);
    }

    staffEndedAfterCurrentPIEndDate(staffEndDate, piEndDate) {
        return moment(staffEndDate).isSameOrAfter(piEndDate);
    }

    staffEndedBeforeCurrentPIEndDate(staffEndDate, piEndDate) {
        if (lodash.isNil(piEndDate) || lodash.isNil(staffEndDate)) {
            return true;
        }
        return moment(staffEndDate).isSameOrBefore(piEndDate);
    }

    assignStaffDelegationsAccordingToPIDelegationWindow(allStaffsDelegations, piStartDate, piEndDate) {
        return lodash.map(allStaffsDelegations, staffDelegation => {
            return this.manipulateDelegationStartAndEndDates(staffDelegation, piStartDate, piEndDate);
        });
    }

    groupStaffDelegationsByPIDelegation(allPIs, doaStudyDelegations) {
        return allPIs.map(pi => {
            const piStartDate = moment(pi.startDate);
            const piEndDate = pi.endDate !== null ? moment(pi.endDate) : null;
            const delegationsUnderCurrentPI = getDelegationsUnderPI(doaStudyDelegations, pi);
            const allStaffsDelegations = delegationsUnderCurrentPI.filter(staff => staff.role !== 'Principal Investigator');
            const relatedStaffs = this.assignStaffDelegationsAccordingToPIDelegationWindow(allStaffsDelegations, piStartDate, piEndDate);
            return lodash.compact([pi].concat(relatedStaffs));
        });
    }

    exportDOALog() {
        const exportDate = moment().format(DATE_FORMAT);

        const margin = 10;
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'letter',
        });
        pdf.setFontSize(9);
        const startingYPosition = 10;
        let currentPdfLocation = startingYPosition;
        currentPdfLocation = this.writeDoAHeader(pdf, currentPdfLocation, margin);
        currentPdfLocation = this.writeStudyKey(pdf, currentPdfLocation, margin);

        currentPdfLocation += 5;
        currentPdfLocation = this.writeStatementOfTestament(pdf, currentPdfLocation, margin);

        let delegations = [];
        let relatedStaffs = [];

        if (this.doaStudyDelegations !== null) {
            delegations = this.doaStudyDelegations;
            let delegationsPerPISession;
            if (this.doaStudyDelegations) {
                const allPIsDelegations = this.getDelegationsByRole(delegations, 'Principal Investigator');
                delegationsPerPISession = this.groupStaffDelegationsByPIDelegation(allPIsDelegations, this.doaStudyDelegations);
            }
            relatedStaffs = delegationsPerPISession;
        }
        let delegationTitle, signatureTitle = '';
        if (this.binder.title) {
            delegationTitle = `Study ${this.binder.title} - Delegation Log`;
            signatureTitle = `Study ${this.binder.title} - Signature Manifestations`;
        }
        else {
            delegationTitle = 'Delegation Log';
            signatureTitle = 'Signature Manifestations';
        }
        const hasRelatedStaffs = !lodash.isEmpty(relatedStaffs);
        if (hasRelatedStaffs) {
            relatedStaffs.map(relatedStaff => {
                currentPdfLocation = startingYPosition;
                pdf.addPage();
                currentPdfLocation = this.writeDelegationTable(pdf, relatedStaff, currentPdfLocation, margin, delegationTitle);
            });
        }
        delegations.forEach(delegation => { // this happens in a separate loop from writing the single table of all delegations to avoid spacing conflicts.
            if (this.delegationHasValidSignatures(delegation)) {
                pdf.addPage();
                currentPdfLocation = 10;
                let delegationArray = [];
                delegationArray.push(delegation); //writeDelegationTable expects an array-- we just want one delegation row, hence the single-item array.
                currentPdfLocation = this.writeDelegationSignatureTable(pdf, delegationArray, currentPdfLocation, margin, signatureTitle);
                currentPdfLocation = this.writeSignaturePage(pdf, delegation, currentPdfLocation, margin);
            }
        });

        this.writePageHeaders(pdf, exportDate);
        const binderTitle = this.binder.title;
        const fileName = `${binderTitle}-delegation-log-${exportDate}.pdf`;
        pdf.save(fileName);
    }
}

export default DoAExport;
